import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Auth/Login';
import Dashboard from './components/Dashboard/Dashboard';
import AttendanceRecords from './components/Attendance/AttendanceRecords';
import OvertimeManagement from './components/Overtime/OvertimeManagement';
import AdminPanel from './components/Admin/AdminPanel';
import ProcessingDashboard from './components/Processing/ProcessingDashboard';
import HRPanel from './components/HR/HRPanel';
import ManagerPanel from './components/Manager/ManagerPanel';
import ReportingPanel from './components/Reporting/ReportingPanel';
import AnalyticsPanel from './components/Analytics/AnalyticsPanel';
import Layout from './components/Layout/Layout';
import LoadingSpinner from './components/Common/LoadingSpinner';
import ChangePasswordModal from './components/Auth/ChangePasswordModal';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  return isAuthenticated ? children : <Navigate to="/login" />;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  if (!user?.is_admin && !user?.is_hr && !user?.is_manager) {
    return <Navigate to="/dashboard" />;
  }
  
  return children;
};

// Manager Route Component
const ManagerRoute = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (!user?.is_manager && !user?.is_hr && !user?.is_admin) return <Navigate to="/dashboard" />;
  return children;
};

// HR/Admin Route Component (stricter - only HR and Admin)
const HRAdminRoute = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  if (!user?.is_admin && !user?.is_hr) {
    return <Navigate to="/dashboard" />;
  }
  
  return children;
};

function AppContent() {
  const { isAuthenticated, loading, user, updateUser } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Router>
      <Routes>
        <Route 
          path="/login" 
          element={isAuthenticated ? <Navigate to="/dashboard" /> : <Login />} 
        />
        <Route path="/" element={<Navigate to="/dashboard" />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/attendance"
          element={
            <ProtectedRoute>
              <Layout>
                <AttendanceRecords />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/overtime"
          element={
            <ProtectedRoute>
              <Layout>
                <OvertimeManagement />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <Layout>
                <AdminPanel />
              </Layout>
            </AdminRoute>
          }
        />
        <Route
          path="/hr"
          element={
            <HRAdminRoute>
              <Layout>
                <HRPanel />
              </Layout>
            </HRAdminRoute>
          }
        />
        <Route
          path="/manager"
          element={
            <ManagerRoute>
              <Layout>
                <ManagerPanel />
              </Layout>
            </ManagerRoute>
          }
        />
        <Route
          path="/processing"
          element={
            <AdminRoute>
              <Layout>
                <ProcessingDashboard />
              </Layout>
            </AdminRoute>
          }
        />
        <Route
          path="/reporting"
          element={
            <HRAdminRoute>
              <Layout>
                <ReportingPanel />
              </Layout>
            </HRAdminRoute>
          }
        />
        <Route
          path="/analytics"
          element={
            <AdminRoute>
              <Layout>
                <AnalyticsPanel />
              </Layout>
            </AdminRoute>
          }
        />
      </Routes>
      {isAuthenticated && user?.must_change_password && (
        <ChangePasswordModal
          forced={true}
          onSuccess={() => updateUser({ must_change_password: false })}
        />
      )}
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
